import React from 'react';
import ErrorDialog from 'components/ErrorDialog';
import { ERRORS } from '../../../../../constants';
import LrGrid from 'components/LrGrid/components/Grid';
import LumenLoader from 'components/Loader';
import { columnDefs } from './GridDefs';
import Spacing from 'components/Layout/components/Spacing';
import { Card, Theme, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { makeStyles } from '@mui/styles';
import { FetchBordereauOptions, fetchMonthlyBordereau } from 'services/reporting';
import { CellClassParams, GridReadyEvent } from 'ag-grid-enterprise';
import { useSelector } from 'react-redux';
import { getAllProgramYearsByProduct, getReportingLocations } from 'application/redux/selectors';
import { IAggFuncParams } from 'ag-grid-enterprise';
import DateRangePickerForm from '../../../../../components/Input/components/DateRangePickerForm';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
    boldBdxCell: {
        fontWeight: 'bold',
    },
}));

const Bordereau: React.FunctionComponent = () => {
    const reportingLocations = useSelector(getReportingLocations);
    const programYears = useSelector(getAllProgramYearsByProduct('COMMERCIAL_PROPERTY'));
    // const [reportingLocations, setReportingLocations] = React.useState([] as ReportingLocation[]);
    const [clickAnchor, setClickAnchor] = React.useState({} as HTMLAnchorElement);
    const [error, setError] = React.useState(false);

    // const fetchLocations = async () => {
    //     try {
    //         const response = await fetchReportingLocations();
    //         setReportingLocations(response);
    //     } catch (e) {
    //         setError(true);
    //     }
    // };

    const distinct = (value: any, index: number, self: any[]) => {
        return self.indexOf(value) === index;
    };

    const uniqueValues = (params: IAggFuncParams) => {
        const distinctValues = params.values.filter(distinct);
        return distinctValues.join(', ');
    };

    const countUnique = (params: IAggFuncParams) => {
        return params.values.filter(distinct).length;
    };

    const onGridReady = (params: GridReadyEvent) => {
        params.api.addAggFuncs({ uniqueValues: uniqueValues, countUnique: countUnique });
        params.api.setGridOption('loading', true);
        // fetchLocations();
    };

    const aggFuncs = {
        uniqueValues: uniqueValues,
        countUnique: countUnique,
    };

    const sideBar = {
        toolPanels: ['columns', 'filters'],
    };

    const classes = useStyles();

    const customDefaultColDefs = {
        resizable: true,
        sortable: true,
        filter: true,
        enablePivot: true,
        cellClass: (params: CellClassParams) => {
            if (params.node.group) {
                return classes.boldBdxCell;
            }
            return '';
        },
    };

    return (
        <>
            <a ref={(input) => input && setClickAnchor(input)} />
            {/* LEGACY: Do not use this pattern. Use the error context (useErrorDialog) instead. */}
            {/* See examples in `src/views/SubmissionDetails/index.tsx` and `src/views/SubmissionDetails/components/QuotesBind/index.tsx` */}
            {error && <ErrorDialog errorTitle={ERRORS.API_ERROR_MSG_TITLE} errorMsg={ERRORS.API_ERROR_MSG_BODY} />}
            <LrGrid
                onGridReady={onGridReady}
                aggFuncs={aggFuncs}
                gridClassName="ag-theme-balham"
                defaultColDef={customDefaultColDefs}
                components={{ lumenLoader: LumenLoader }}
                loadingOverlayComponent="lumenLoader"
                sideBar={sideBar}
                suppressColumnVirtualisation
                groupTotalRow={'bottom'}
                grandTotalRow={'bottom'}
                animateRows
                columnDefs={columnDefs}
                rowData={reportingLocations}
                autosize={true}
                autoGroupColumnDef={{
                    pinned: 'left',
                    lockPosition: true,
                }}
            />
            <Spacing height="36px" />
            <Card>
                <Grid container spacing={0} alignItems="center">
                    <Grid xs={6}>
                        <Typography variant="h4">Bordereau Reports</Typography>
                    </Grid>
                </Grid>
                <Spacing height="24px" />

                <DateRangePickerForm
                    onSubmit={async (values) => {
                        const fetchOptions: FetchBordereauOptions = {
                            startDate: values.startDate,
                            endDate: values.endDate,
                            contentType: values.option?.contentType,
                            reportEntity: values.option?.entity,
                            qualifier: values.qualifier,
                        };
                        fetchOptions.contentType = values.option?.contentType;
                        const report = await fetchMonthlyBordereau(fetchOptions);
                        let fileType = '.json';
                        const contentType = report.headers.get('Content-Type');
                        switch (contentType) {
                            case 'text/csv':
                                fileType = '.csv';
                                break;
                            case 'text/tab-separated-values':
                                fileType = '.tsv';
                                break;
                            default:
                                break;
                        }
                        const blob = await report.blob();
                        const url = URL.createObjectURL(blob);
                        const fileDate = values.option?.hideStartDate
                            ? values.option?.hideEndDate
                                ? moment()
                                : values.endDate
                            : values.startDate;
                        clickAnchor.href = url;
                        clickAnchor.download =
                            (fileDate ? fileDate.format('MM/DD/YYYY') + ' ' : '') +
                            ' ' +
                            (values.option?.filename || 'Bordereaux') +
                            fileType;
                        clickAnchor.click();
                        URL.revokeObjectURL(url);
                    }}
                    submitText={'Download Report'}
                    options={[
                        {
                            label: 'Transverse',
                            entity: 'Transverse',
                            contentType: 'text/csv',
                            filename: 'Transverse Bordereaux',
                        },
                        {
                            label: 'Ally',
                            entity: 'ALLY',
                            contentType: 'text/csv',
                            filename: 'Ally Bordereaux',
                        },
                        {
                            label: 'Policies In Force',
                            entity: 'POLICIES_IN_FORCE',
                            contentType: 'text/csv',
                            filename: 'PoliciesInForce',
                            qualifiers: programYears
                                .sort((t1, t2) => {
                                    return moment(t1.effectiveDate, 'MM/DD/YYYY', true).isBefore(
                                        moment(t2.effectiveDate, 'MM/DD/YYYY', true)
                                    )
                                        ? 1
                                        : -1;
                                })
                                .map((t) => t.programName),
                            qualifierLabel: 'Treaty',
                            hideEndDate: true,
                            hideStartDate: true,
                        },
                        {
                            label: 'Accelerant',
                            entity: 'Accelerant',
                            contentType: 'text/csv',
                            filename: 'Accelerant Bordereaux',
                        },
                        {
                            filename: 'Modeling Data',
                            label: 'Modeling Data',
                            hideStartDate: true,
                            entity: 'MODELING_DATA',
                            contentType: 'text/csv',
                        },
                        {
                            filename: 'CurrentBook',
                            label: 'Current Book',
                            hideStartDate: true,
                            entity: 'CURRENT_BOOK',
                            contentType: 'text/csv',
                        },
                    ]}
                />
            </Card>
        </>
    );
};

export default Bordereau;
